import {
    Component,
    ElementRef,
    Inject,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Optional,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';
import { bindPosition, Placement, Strategy } from '@cmps/cdk';
import {
    CMPS_DIALOG_DATA,
    CmpsTemplatePortal,
    CmpsTemplatePortalHandle,
} from '@cmps/ng-uikit';
import { TranslateService } from '@ngx-translate/core';
import {
    addDays,
    addMonths,
    addWeeks,
    differenceInDays,
    eachDayOfInterval,
    endOfDay,
    endOfMonth,
    format,
    isAfter,
    isSameMonth,
    subDays,
} from 'date-fns';
import { animationFrameScheduler, Subject, timer } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { CmpsDatePipe } from '../../common/date.pipe';
import { CmpsDecimalPipe } from '../../common/number.pipe';
import { AxisData } from '../chart/axis-data';
import { AxisOptions, AxisPointMouseEvent } from '../chart/chart';

import { TrendDialogData } from './model/trend-dialog-data.model';
import { TrendDialogMode } from './model/trend-dialog-mode.model';
import { TranslationKeyMap } from './trend-dialog.i18n';

interface Context {
    data: AxisPointMouseEvent;
}

@Component({
    selector: 'cmps-mi2-trend-dialog',
    templateUrl: './trend-dialog.component.html',
    host: {
        '[class.cmps-mi2-trend-without-dialog]': '!_isDialog',
        class: 'cmps-mi2-trend-dialog',
    },
})
export class TrendDialogComponent implements OnChanges, OnInit, OnDestroy {
    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _TrendDialogMode = TrendDialogMode;
    @ViewChild('callout') private readonly _callout: TemplateRef<Context>;
    /** @private */ _chartOptions: AxisOptions;
    /** @private */ _chartData: AxisData;
    /** @private */ _isDialog: boolean;

    private _hdl: CmpsTemplatePortalHandle<Context>;
    private _hdlSubj: Subject<void>;

    constructor(
        private readonly _el: ElementRef<HTMLElement>,
        private readonly _ngZone: NgZone,
        private readonly _viewContainerRef: ViewContainerRef,
        private readonly _translateService: TranslateService,
        private readonly _datePipe: CmpsDatePipe,
        private readonly _decimalPipe: CmpsDecimalPipe,
        private readonly _portal: CmpsTemplatePortal,
        @Optional()
        @Inject(CMPS_DIALOG_DATA) /** @private */
        readonly _data: TrendDialogData,
    ) {}

    ngOnInit(): void {
        this._isDialog =
            [
                TrendDialogMode.INSIGHT_TREND,
                TrendDialogMode.MI2,
                TrendDialogMode.PERFORMANCE_DATA,
            ].includes(this._data.mode) === false;

        this._renderChartOnNextAnimationFrame();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes.data != null &&
            changes.data.firstChange === false &&
            changes.data.previousValue !== changes.data.currentValue
        ) {
            // _data has changed, re-render the chart
            this._renderChart();
        }
    }

    ngOnDestroy(): void {
        this._onPointMouseOut();
    }

    /** @private */
    _onPointMouseOver($event: AxisPointMouseEvent): void {
        // TODO change to use overlay from uikit
        // const rect = this._el.nativeElement.getBoundingClientRect();
        // this._calloutData = $event;
        // this._callout.nativeElement.style.display = 'block';
        // this._callout.nativeElement.style.top = `${$event.pointRect.top - rect.top + $event.pointRect.height + 10}px`;
        // this._callout.nativeElement.style.left = `${$event.pointRect.left - rect.left}px`;

        this._onPointMouseOut();
        const { hdl, cancelSubj } = this._makePortal<Context>(
            this._callout,
            { data: $event },
            $event.el as HTMLElement,
            'bottom-left',
        );
        this._hdl = hdl;
        this._hdlSubj = cancelSubj;
    }

    /** @private */
    _onPointMouseOut(): void {
        // this._callout.nativeElement.style.display = 'none';
        // this._calloutData = null;

        this._hdlSubj?.next();
        this._hdlSubj?.complete();
        this._hdlSubj = null;

        this._hdl?.destroy();
        this._hdl = null;
    }

    private _renderChart(): void {
        // TODO cleanup
        this._chartOptions = {
            rect: this._data.rect ?? { width: '100%', height: 400, top: 20 },
            transitionDuration: () => {
                if (!this._isDialog) {
                    return 0;
                }
                return null;
            },
            overrideDates: () => {
                if (this._data.mode === TrendDialogMode.PERFORMANCE_DATA) {
                    const startDate = this._chartData.paths[0].points[0].x;
                    const endDate =
                        this._chartData.paths[
                            this._chartData.paths.length - 1
                        ].points.slice(-1)[0].x;
                    return eachDayOfInterval({
                        start: startDate,
                        end: endDate,
                    });
                }
                return null;
            },
            showYAxisLabel2: this._data.showYAxisLabel2,
            overridePaddedDomain: (domain: [Date, Date]) => {
                if (this._data.mode === TrendDialogMode.PERFORMANCE_DATA) {
                    return [domain[0], addWeeks(domain[1], 1)];
                }
                return null;
            },
            overrideXAxisTicks: (domain: [Date, Date]) => {
                if (this._data.mode === TrendDialogMode.PERFORMANCE_DATA) {
                    const tickData: Date[] = [];
                    const end = addDays(domain[1], 1);
                    let next = endOfMonth(domain[0]);
                    while (next < end) {
                        tickData.push(next);
                        next = endOfMonth(addMonths(subDays(next, 15), 1));
                    }
                    const endDay = endOfDay(domain[1]);
                    const lastTick = tickData[tickData.length - 1];
                    if (
                        !isSameMonth(endDay, lastTick) &&
                        isAfter(endDay, lastTick) &&
                        /* prevent label overlap when days are too close */
                        differenceInDays(endDay, lastTick) > 3
                    ) {
                        tickData.push(endDay);
                    }
                    return tickData;
                }
                return null;
            },
            yAxisTickLabel: (d) => {
                if (!this._isDialog) {
                    return format(d, 'MMM yy');
                }
                return null;
            },
            strokeColor: (d, i) => d[i].color,
        };

        if ((this._data.paths?.[0]?.points?.length ?? 0) === 0) {
            return;
        }

        this._chartData = {
            leftTicks: this._data.leftTicks,
            rightTicks: this._data.rightTicks,
            paths: this._data.paths,
        };
    }

    private _makePortal<T>(
        templateRef: TemplateRef<T>,
        context: T,
        target: HTMLElement,
        placement: Placement,
    ): { hdl: CmpsTemplatePortalHandle<T>; cancelSubj: Subject<void> } {
        const hdl = this._portal.create<T>(
            templateRef,
            this._viewContainerRef,
            context,
        );
        hdl.ref.detectChanges();

        const cancelSubj = new Subject<void>();

        /// //
        const posFn = bindPosition(
            Strategy.ADJACENT,
            hdl.wrapper,
            target,
            placement,
            (pos) => {
                Object.keys(pos.style).forEach((k) => {
                    hdl.wrapper.style[k] = pos.style[k];

                    // bindPosition should support padding the size of the target rect, or overriding the target rect
                    if (k === 'top') {
                        hdl.wrapper.style.top = `${
                            parseInt(pos.style[k]) + 10
                        }px`;
                    }
                });
                hdl.wrapper.style.zIndex = '10000';
            },
            cancelSubj,
        );
        this._ngZone.onStable
            .pipe(
                tap(() => {
                    posFn();
                }),
                takeUntil(cancelSubj),
            )
            .subscribe();
        /// //

        return { hdl, cancelSubj };
    }

    private _renderChartOnNextAnimationFrame(): void {
        timer(0, null, animationFrameScheduler)
            .pipe(
                tap(() => {
                    this._renderChart();
                }),
            )
            .subscribe();
    }
}
